import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCalendlyEventListener } from 'react-calendly';
import { EnumLinkedInFlow } from 'tdc-web-backend/enums/enums';
import { Card, Stack, Typography } from '@mui/material';
import { ArrowRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useCreate from '../../../../../../../utils/hooks/crud-hooks/useCreate';
import ControlledTextInput from '../../../../../../../components/custom-inputs/ControlledTextInput';
import ControlledCheckboxInput from '../../../../../../../components/custom-inputs/ControlledCheckboxInput';
import CalendlyModal from '../../components/CalendlyModal';
import ReviewModal from '../../components/ReviewModal';
import CustomButton from '../../../../../../../components/button/CustomButton';
import useGetOne from '../../../../../../../utils/hooks/crud-hooks/useGetOne';
import useNotification from '../../../../../../../utils/hooks/useNotification';

const GeneralInputs = ({
  caseStudyId,
  token,
  contentId,
}: {
  caseStudyId: string;
  token: string;
  contentId: string;
}) => {
  const { t, i18n } = useTranslation();
  const { formState, getValues, trigger } = useFormContext();
  const [openModal, setIsOpenModal] = useState(false);
  const [isLoadingLinkedIn, setIsLoadingLinkedIn] = useState(false);
  const [showActionBtn, setShowActionBtn] = useState(false);
  const [openReviewModal, setIsOpenReviewModal] = useState(false);
  const { notify } = useNotification();
  const handleConfirm = () => {
    setIsOpenModal(true);
    setIsOpenReviewModal(false);
  };

  const { mutate: submit, isLoading } = useCreate({
    resource: `/case-studies/${caseStudyId}/submit-review?token=${token}`,
  });

  const submitReview = () => {
    const data = getValues();
    submit({
      ...data,
      //   caseStudyContent: contentId,
    });
  };

  useCalendlyEventListener({
    onEventScheduled: (e) => {
      setShowActionBtn(true);
      submitReview();
    },
  });

  const { refetch } = useGetOne<{ authorizationUrl: string }>({
    resource: `auth/linkedin/authorization-url?flow=${EnumLinkedInFlow.CSReview}`,
    enabled: false,
    onSuccess: (data) => {
      const review = {
        caseStudyId,
        token,
        reviewData: getValues(),
      };
      localStorage.setItem('review', JSON.stringify(review));
      window.location.href = data.data.authorizationUrl;
      // setIsLoadingLinkedIn(false);
    },
    onError: () => {
      setIsOpenReviewModal(false);
      setIsLoadingLinkedIn(false);
      notify.error(t('errors.somethingWentWrong'));
    },
  });

  return (
    <Stack
      spacing={4}
      // width={'40%'}
      sx={{ width: { xs: '100%', md: '40%' }, border: '1px solid', borderColor: 'primaryDark.200' }}
      p={3}
      pt={{ xs: 10, md: 20 }}
    >
      <Typography variant="heading3" color="primaryDark.600">
        {t('caseStudies.reviewerInformation')}
      </Typography>

      <ControlledTextInput
        required
        name="reviewer.firstName"
        placeholder={t('common.firstName')}
        label={t('common.firstName')}
      />
      <ControlledTextInput
        required
        name="reviewer.lastName"
        placeholder={t('common.lastName')}
        label={t('common.lastName')}
      />
      <ControlledTextInput
        required
        name="reviewer.jobTitle"
        placeholder={t('common.jobTitle')}
        label={t('common.jobTitle')}
      />
      <ControlledTextInput
        required
        name="reviewer.companyName"
        placeholder={t('common.companyName')}
        label={t('common.companyName')}
      />
      <Card
        elevation={0}
        sx={{
          backgroundColor: 'primaryDark.150',
          p: 1,
        }}
      >
        <ControlledCheckboxInput
          name="reviewer.isAnonymous"
          label={
            <Typography ml={1} variant="body3" color="primaryDark.600">
              {t('caseStudies.anonymousPreference')}
            </Typography>
          }
        />
      </Card>
      <Card
        elevation={0}
        sx={{
          backgroundColor: 'primaryDark.200',
          p: 2,
        }}
      >
        <Typography ml={1} variant="body3" color="primaryDark.500">
          {t('caseStudies.reviewFormInfo')}
        </Typography>
      </Card>
      <CalendlyModal
        open={openModal}
        showActionBtn={showActionBtn}
        caseStudyId={caseStudyId}
        handleCancel={() => {
          setIsOpenModal(false);
          setIsOpenReviewModal(true);
        }}
      />
      <ReviewModal
        open={openReviewModal}
        handleConfirm={handleConfirm}
        handleCancel={() => setIsOpenReviewModal(false)}
        isLoading={isLoadingLinkedIn}
        handleLinkedIn={() => {
          setIsLoadingLinkedIn(true);
          refetch();
        }}
      />

      {!formState.isValid ? (
        <CustomButton
          onClick={() => {
            trigger();
          }}
          endIcon={<ArrowRight fill="white" />}
        >
          {t('caseStudies.submitReview')}
        </CustomButton>
      ) : (
        <CustomButton
          loading={isLoading}
          onClick={() => setIsOpenReviewModal(true)}
          endIcon={<ArrowRight fill="white" />}
        >
          {t('caseStudies.submitReview')}
        </CustomButton>
      )}
    </Stack>
  );
};

export default GeneralInputs;
